import React from 'react';
import { useSelector } from 'react-redux';
import _orderBy from 'lodash/orderBy';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';

// Store
import {
  $allSurveys,
  $indicatorSelected,
  $indicatorsObject,
  $surveySelected,
} from '../../store/slices/admin2EstimatesSlice';
import { $adminSelected, $fieldSelected } from '../../store/slices/sidebarMapFiltersSlice';

// Utils
import { getIndicatorKey } from '../../utils/indicators.utils';
import { getRegionNameFormatted } from '../../utils/popup.utils';
import { getAdmin2dataFilteredSorted } from '../../utils/admin2Data.utils';

// Configs
import { ADMIN__Admin1, SIDEBAR_MAP_FILTER_FIELDS_OBJECT } from '../../configs/map.configs';
import { POPUP_MARKER_COLOR } from '../../configs/popup.config';
import { COLUMN_CHARACTERISTIC } from '../../configs/tools.configs';
import { ATTRIBUTE_FIELD_FOR_ESTIMATES, ATTRIBUTE_FIELD_FOR_POPULATION } from '../../configs/attributeFields.configs';

// Styles
import './Admin2Chart.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

function Admin2Chart() {
  const surveySelected = useSelector($surveySelected);
  const indicatorKey: string = getIndicatorKey();
  const indicatorsObject = useSelector($indicatorsObject);
  const indicatorSelected = useSelector($indicatorSelected);
  const adminSelected = useSelector($adminSelected);
  const allSurveys = useSelector($allSurveys);
  const fieldSelected = useSelector($fieldSelected);

  let chartLabels = [];
  let chartValues = [];
  let data;
  let admin1dataFilteredSorted: any[];
  let admin2dataFilteredSorted: any[];

  if (adminSelected === ADMIN__Admin1) {
    const indicatorsObjectByIndicatorKey = _orderBy(indicatorsObject[indicatorKey], [COLUMN_CHARACTERISTIC]);
    if (indicatorsObjectByIndicatorKey) {
      admin1dataFilteredSorted = Object.values(indicatorsObjectByIndicatorKey);
      chartLabels = admin1dataFilteredSorted.map((item: any) => item.CharacteristicLabel);
      chartValues = admin1dataFilteredSorted.map((item: any) => item.Value);
    }

    if (surveySelected) {
      data = {
        labels: chartLabels,
        datasets: [
          {
            label: `${surveySelected.value} - ${indicatorSelected.label}`,
            data: chartValues,
            backgroundColor: POPUP_MARKER_COLOR,
          },
        ],
      };
    }
  } else {
    if (allSurveys) {
      admin2dataFilteredSorted = getAdmin2dataFilteredSorted(indicatorSelected, allSurveys, surveySelected);
      const admin2data = admin2dataFilteredSorted.map((survey: any) => {
        return {
          label: survey.attributes.adm2_name,
          value: survey.attributes[fieldSelected],
        };
      });

      if (admin2data) {
        chartLabels = admin2data.map((item: any) => item.label);
        chartValues = admin2data.map((item: any) => item.value);
        data = {
          labels: chartLabels,
          datasets: [
            {
              label: `${surveySelected?.value} - ${indicatorSelected.label}`,
              data: chartValues,
              backgroundColor: POPUP_MARKER_COLOR,
            },
          ],
        };
      }
    }
  }

  /**
   * Function to get the maximum value for the x-axis of the bar chart.
   * If the selected field is not population, it returns 100.
   * @returns {number | undefined} The maximum value for the x-axis.
   */
  function getAxisMaxValue(): number | undefined {
    if (fieldSelected !== ATTRIBUTE_FIELD_FOR_POPULATION) {
      return 100;
    }
  }

  const chartJsBarOptions = {
    maintainAspectRatio: false,
    indexAxis: 'y',
    animation: {
      duration: 0,
    },
    layout: {
      padding: {
        right: 45,
      },
    },
    scales: {
      x: {
        max: getAxisMaxValue(),
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: () => null,
          label: (context: any) => {
            const selectedFormat = SIDEBAR_MAP_FILTER_FIELDS_OBJECT[fieldSelected].format;
            const data = context.raw;
            const dataFormatted = selectedFormat(parseFloat(data));
            let item;
            let regionNameFormatted;

            if (adminSelected === ADMIN__Admin1) {
              item = admin1dataFilteredSorted[context.dataIndex];
              regionNameFormatted = getRegionNameFormatted(item.CharacteristicLabel);

              return `${regionNameFormatted}: ${dataFormatted}`;
            } else {
              item = admin2dataFilteredSorted[context.dataIndex];
              const admin1Name: string = item.attributes?.adm1_name;
              const admin2Name: string = item.attributes?.adm2_name;
              const uncertaintyLower: string = item.attributes?.lower_ci;
              const uncertaintyUpper: string = item.attributes?.upper_ci;
              const uncertaintyLowerFormatted = selectedFormat(parseFloat(uncertaintyLower));
              const uncertaintyUpperFormatted = selectedFormat(parseFloat(uncertaintyUpper));
              regionNameFormatted = getRegionNameFormatted(admin1Name, admin2Name);

              if (fieldSelected !== ATTRIBUTE_FIELD_FOR_POPULATION) {
                const population: string = item.attributes?.pop_estimate;
                const populationFormatted = SIDEBAR_MAP_FILTER_FIELDS_OBJECT[ATTRIBUTE_FIELD_FOR_POPULATION].format(
                  parseFloat(population)
                );

                if (fieldSelected === ATTRIBUTE_FIELD_FOR_ESTIMATES) {
                  return `${regionNameFormatted}: ${dataFormatted} - Population: ${populationFormatted}`;
                } else {
                  return `${regionNameFormatted}: ${dataFormatted} (CI: ${uncertaintyLowerFormatted} .. ${uncertaintyUpperFormatted}) - Population: ${populationFormatted}`;
                }
              } else {
                return `${regionNameFormatted}: ${dataFormatted}`;
              }
            }
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'right',
        font: {
          weight: 'bold',
        },
        formatter: function (value: string) {
          const format = SIDEBAR_MAP_FILTER_FIELDS_OBJECT[fieldSelected].format;
          return format(parseFloat(value));
        },
      },
    },
  };

  const CHART_ROW_HEIGHT = 30;
  const CHART_ROWS_MIN = 15;
  return (
    <section className='Admin2Chart__wrapper row'>
      {surveySelected && data && (
        <div
          className='Admin2Chart__container col-lg-12 col-md-12'
          style={{
            height:
              data?.labels?.length > CHART_ROWS_MIN
                ? data?.labels?.length * CHART_ROW_HEIGHT
                : CHART_ROWS_MIN * CHART_ROW_HEIGHT + 50,
          }}
        >
          <Bar options={chartJsBarOptions} data={data} />
        </div>
      )}
    </section>
  );
}

export default Admin2Chart;
